import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable, OnDestroy } from "@angular/core";
import moment from "moment";
import { IMqttMessage, MqttService } from "ngx-mqtt";
import { BehaviorSubject, Observable, of, Subject, Subscription } from "rxjs";
import { filter, map, switchMap, withLatestFrom } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { Address } from "src/components/shared-components/class/component-classes/address";
import { iAddressSearch } from "src/components/shared-components/interfaces/iAddress-search";
import { AppReserved } from "src/model/class/general classes/app-reserved";
import { AppSettings } from "src/components/shared-components/class/general-classes/app-settings";
import {
  GenericApiData,
  GenericApiDataSimple,
} from "src/components/shared-components/class/general-classes/generic-api-data";
import { AddressDefaultMethods } from "src/components/shared-components/enum/enumAddress";
import { AccessControlGatewaySubgroup } from "src/components/shared-components/enum/enumGateway";
import { LogLevel } from "src/components/shared-components/enum/enumLog";
import { iEvent } from "src/components/shared-components/interfaces/iEvent";
import { iMqttHandling } from "src/model/interface/iMqtt-handling";
import { EventService } from "./event.service";
import { LoggerService } from "./logger.service";
import { MqttClientService } from "./mqtt-client.service";
import { Store } from "@ngrx/store";
import { isUserAuthenticated } from "src/state/selectors/authentication.selectors";
import { TreeNodeGateway } from "src/model/class/general classes/tree-node-gateway";
import { addressTreeLoadInitStartAction } from "src/state/actions/address.action";
import { DateTimeFormats } from "src/components/shared-components/class/general-classes/date-time-formats";
import { MomentFormats } from "src/model/class/general classes/moment-formats";

@Injectable({
  providedIn: "root",
})
export class AddressService implements OnDestroy, iEvent, iMqttHandling {
  private _logContext: string = "Address.service";
  private _addressStore: Map<string, Address> = new Map<string, Address>(null);

  private _isErrorInGetAddressListAPIRequest: boolean = false;

  _mqttAddressSubscriptions: Array<Subscription> = null;

  //DUMMY
  _mqttAddressSubscriptionsMap: Map<string, Subscription> = null;

  _mqttExternalAddressSubscriptionsMap: Map<string, Subscription> = null;

  _eventSubscriptions: Array<Subscription> = null;

  private _needToInitialize: boolean = true;
  private _addressTreeStore: GenericApiData = null;
  private _addressTreeSubject: BehaviorSubject<Array<TreeNodeGateway>> =
    new BehaviorSubject<Array<TreeNodeGateway>>(null);

  get isErrorInGetAddressListAPIRequest(): boolean {
    return this._isErrorInGetAddressListAPIRequest;
  }

  //AB: just subject instead of behaviour subject beacuse we don't need to get the story of addresses, just last value
  public addressUpdate: Subject<Address> = new Subject<Address>();

  constructor(
    private _mqttService: MqttClientService,
    private _eventService: EventService,
    private _http: HttpClient,
    private _loggerService: LoggerService,
    private _mqttServiceNative: MqttService,
    private _store: Store,
  ) {
    this._mqttAddressSubscriptions = new Array<Subscription>();

    this._mqttExternalAddressSubscriptionsMap = new Map<string, Subscription>();

    this.subscribeEvents();
  }

  ngOnDestroy() {
    this.unsubscribeEvents();

    this.unsubscribeToMQTTAddresses();
  }

  //#region Subscribe to event-service observable
  subscribeEvents(): void {
    this._eventSubscriptions = new Array<Subscription>();

    this._eventSubscriptions.push(
      this._eventService.OnMakeLogout.subscribe(async () => {
        this._addressTreeStore = null;
        this._addressTreeSubject.next(null);

        await this.unsubscribeToMQTTAddresses();

        this._loggerService.log(
          LogLevel.LOG_INFO,
          this._logContext,
          "_eventService",
          "OnMakeLogout",
          "NARTIVE ARRAY 2.",
        );
        for (const entry of Array.from(
          new Map(
            Object.entries(this._mqttServiceNative.observables),
          ).entries(),
        )) {
          this._loggerService.log(
            LogLevel.LOG_INFO,
            this._logContext,
            "_eventService",
            "OnMakeLogout",
            "NARTIVE ARRAY 2 Entry:  " + entry[0],
          );
        }
      }),
    );

    this._eventSubscriptions.push(
      this._eventService.OnComeBackOnline.pipe(
        withLatestFrom(this._store.select(isUserAuthenticated)),
        filter((combinedArray: Array<any>) => combinedArray[1]),
        map((combinedArray: Array<any>) => combinedArray[0]),
      ).subscribe(() => {
        this.reloadServiceData();
      }),
    );
  }

  //#region Address tree
  /**
   *  Initialize public observable list trasmission:
   *  it should be call in the costructor of all component/page that you need.
   */
  public Init(): void {
    this._store.dispatch(addressTreeLoadInitStartAction());
    if (this._needToInitialize) {
      this._needToInitialize = false;
    }
  }

  /**
   * Get addressTree about a specific gateway, considering user informations
   *
   * @returns
   */
  public getAddressTree(iduser: number): Observable<GenericApiData> {
    const queryString: string = "?userid=" + iduser;

    return this._http.get<GenericApiData>(
      environment.httpServerRoot +
        AppSettings.API_URL_ADDRESS_TREE +
        queryString,
    );
  }

  /**
   * Get last modifed address tree
   *
   * @param addressTreeStore Area store
   * @returns
   */
  public getAddressTreeByLastModified(
    iduser: number,
    addressTreeStore: GenericApiData,
  ): Observable<GenericApiData> {
    const lastAPIModifiedString = addressTreeStore?.lastAPIModified || "";
    const queryString: string =
      "?userid=" +
      iduser +
      "&lastModified=" +
      encodeURIComponent(lastAPIModifiedString);

    return this._http
      .get<GenericApiData>(
        environment.httpServerRoot +
          AppSettings.API_URL_ADDRESS_TREE +
          queryString,
      )
      .pipe(
        map((returnAreaStore: GenericApiData) => {
          if (returnAreaStore) {
            addressTreeStore = returnAreaStore;
          }

          return addressTreeStore;
        }),
      );
  }

  //#endregion

  /**
   * Reload service data (es. in case of error)
   */
  public reloadServiceData() {
    this.Init();
  }

  unsubscribeEvents(): void {
    for (let sub of this._eventSubscriptions) {
      sub.unsubscribe();
    }
  }
  //#endregion

  /**
   * GF: return the object associated to a specific address
   *
   * @param instanceGUID InstanceGUID to get address
   * @param address Address to get
   */
  public getAddressByInstanceGUIDAndAddress(
    instanceGUID: string,
    address: string,
  ): Address {
    return this.getAddressByFullAddress(instanceGUID + "_" + address);
  }

  /**
   * Return Address from Address store by full key (INSTANCEGUID_ADDRESS)
   *
   * @param fullInstanceGUIDAddress Full Address key (INSTANCEGUID_ADDRESS)
   * @returns
   */
  public getAddressByFullAddress(
    fullInstanceGUIDAddress: string,
  ): Address | null {
    if (this._addressStore.get(fullInstanceGUIDAddress) != null)
      return new Address(this._addressStore.get(fullInstanceGUIDAddress));
    return null;
  }

  /**
   * Get formatted full address of access control gateway with current format INSTANCEGUID_ADDRESS
   *
   * @param instanceGuid Gateway instanceguid
   * @param idDevice ID Device
   * @param property Device property
   * @returns
   */
  public getFullAddressByIDDeviceAndProperty(
    instanceGuid: string,
    idDevice: number,
    property: number,
  ): string {
    return (
      instanceGuid + "_" + this.getVirtualAccessAddress(idDevice, property)
    );
  }

  /**
   * Get formatted address of access control gateway with current format (only address)
   *
   * @param idDevice ID Device of Access control gateway
   * @param property Device propertu
   * @returns
   */
  public getVirtualAccessAddress(
    idDevice: number,
    property: AccessControlGatewaySubgroup,
  ): string {
    return "ACCESSCONTROL/" + idDevice + "/" + property;
  }

  /**
   * Get middlegroup of generic address by address
   *
   * @param address Address to get middlegrou
   * @returns
   */
  public getMiddleGroupAddressFromAddress(address: string): string {
    return address.split("/")?.[1] || "";
  }

  /**
   * API call to get addresses for smart component
   * @param queryString string to get addresses
   * @returns
   */
  public getAddressesForSmartComponent(
    queryString: string,
  ): Observable<GenericApiDataSimple> {
    return this._http.get<GenericApiDataSimple>(
      environment.httpServerRoot +
        AppSettings.API_URL_ADDRESS +
        "?getSimpleData=true&" +
        queryString,
    );
  }

  /**
   * Change data into an array of addresses
   * @param returnAddressList data returned from API cal
   * @returns
   */
  public changeDataIntoAddressesList(
    returnAddressList: GenericApiData,
  ): Address[] {
    if (returnAddressList.data.length > 0) {
      const addresses: Array<Address> = new Array<Address>();
      for (let i = 0; i < returnAddressList.data.length; i++) {
        if (
          addresses.findIndex(
            (a) =>
              a.instanceGUID == returnAddressList.data[i].instanceGUID &&
              a.address == returnAddressList.data[i].address,
          ) < 0
        ) {
          addresses.push(returnAddressList.data[i]);
          this.addAddress(returnAddressList.data[i]);
        }
      }
      return addresses;
    } else {
      return new Array<Address>();
    }
  }

  /**
   *This method retieves from server a list of "Address" objects. This list is calculated by a query composed using a serie of variable data.
   * @param AddressSearchObj obj with the info to search
   * @returns
   */
  public async getAddressesByInstanceGUIDAndAddressAndName(
    AddressSearchObj: iAddressSearch,
  ): Promise<Address | Address[]> {
    this._loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      this._logContext,
      "getAddressesByInstanceGUIDAndAddressAndName()",
      "Address to search: " + AddressSearchObj.toString(),
    );

    // Initialize Params Object //
    let params = new HttpParams();
    params = params.append("InstanceGUID", AddressSearchObj.Instanceguid);
    if (AddressSearchObj.Address !== "")
      params = params.append("Address", AddressSearchObj.Address);
    if (AddressSearchObj.Name !== "")
      params = params.append("Name", AddressSearchObj.Name);
    if (AddressSearchObj.Language !== "")
      params = params.append("language", AddressSearchObj.Language);

    const returnAddress = await this._http
      .get<GenericApiData>(
        environment.httpServerRoot + AppSettings.API_URL_ADDRESS,
        { params: params },
      )
      .pipe(
        switchMap((returnAddressList: GenericApiData) => {
          if (returnAddressList.data.length > 0) {
            const addresses: Array<Address> = new Array<Address>();
            for (let i = 0; i < returnAddressList.data.length; i++) {
              if (
                addresses.findIndex(
                  (a) =>
                    a.instanceGUID == returnAddressList.data[i].instanceGUID &&
                    a.address == returnAddressList.data[i].address,
                ) < 0
              ) {
                addresses.push(returnAddressList.data[i]);
                this.addAddress(returnAddressList.data[i]);
              }
            }

            return of(addresses);
          } else {
            return new Array<Address>();
          }
        }),
      )
      .toPromise()
      .then(
        (addresses) => {
          return addresses;
        },
        (error) => {
          this._loggerService.log(
            LogLevel.LOG_ERROR,
            this._logContext,
            this._logContext,
            "getAddressesByInstanceGUIDAndAddressAndName()", //
            "ERROR IN API CALL: " + error.message,
          );
          return null;
        },
      );

    return returnAddress;
  }

  /**
   * Get address by id
   * @param idAddress id address
   */
  public async getAddressById(idAddress: number): Promise<Address | Address[]> {
    let params = new HttpParams();
    params = params.append("IdAddress", idAddress);

    const returnAddress = await this._http
      .get<GenericApiData>(
        environment.httpServerRoot + AppSettings.API_URL_ADDRESS,
        { params: params },
      )
      .pipe(
        switchMap((returnAddressList: GenericApiData) => {
          if (returnAddressList.data.length > 0) {
            const addresses: Array<Address> = new Array<Address>();
            for (let i = 0; i < returnAddressList.data.length; i++) {
              addresses.push(returnAddressList.data[i]);
            }
            return of(addresses);
          } else {
            return new Array<Address>();
          }
        }),
      )
      .toPromise()
      .then(
        (address) => {
          return address;
        },
        (error) => {
          this._loggerService.log(
            LogLevel.LOG_ERROR,
            this._logContext,
            this._logContext,
            "getAddressById()",
            "ERROR IN API CALL: " + error.message,
          );
          return null;
        },
      );

    return returnAddress;
  }

  /**
   * Add list addresses to service store:
   * if address if not subscribed to mqtt service, it create subscription, otherwiser it updates store
   *
   * @param listAddress List adress to verify existange
   */
  public async addAddresses(listAddress: Array<Address>): Promise<void> {
    for (const address of listAddress) {
      await this.addAddress(address, false);
    }
  }

  /**
   * Add single addresses to service store:
   * if address if not subscribed to mqtt service, it create subscription, otherwiser it updates store
   *
   * @param address Address to verify existence
   * @param propagateUpdate Flag who handle propagation to subject
   */
  public async addAddress(address: Address, propagateUpdate: boolean = true) {
    const keyAddress: string = address.instanceGUID + "_" + address.address;

    if (!this._addressStore.has(keyAddress)) {
      this._loggerService.log(
        LogLevel.LOG_INFO,
        this._logContext,
        this._logContext,
        "addAddress()",
        "Adress not present: " + keyAddress,
      );

      this._addressStore.set(keyAddress, address);
      await this.handleMQTTSubscriptioByAddress(address);
    } else {
      this._loggerService.log(
        LogLevel.LOG_INFO,
        this._logContext,
        this._logContext,
        "addAddress()",
        "Address already present in local map: " + keyAddress,
      );

      if (this._addressStore.get(keyAddress).lastValue !== address.lastValue) {
        this._loggerService.log(
          LogLevel.LOG_INFO,
          this._logContext,
          this._logContext,
          "addAddress()",
          "Address already present in local map: " +
            keyAddress +
            " | BUT DIFFERENT VALUE. OLD: " +
            this._addressStore.get(keyAddress).lastValue +
            " | NEW: " +
            address.lastValue,
        );
        this.addressUpdate.next(address);
      }

      this._addressStore.delete(keyAddress);
      this._addressStore.set(keyAddress, address);
    }

    if (propagateUpdate) {
      /* CURRENTLY THIS FEATURE IS NOT USED */
      //this._addressSubject.next(this._addressStore);
    }
  }

  /**
   * Check if specific address is registered for subscription
   *
   * @param instanceGUID instanceGuid address to check
   * @param address address to check
   * @returns return boolean value that expain address is register for subscription
   */
  public isAddressSubscribed(instanceGUID: string, address: string): boolean {
    const addressKey = instanceGUID + "_" + address;

    return this._addressStore.has(addressKey);
  }

  /**
   * Update specific address value and state with specific value
   *
   * @param address Address object to update
   * @param currentValue Value to apply to address
   * @param currentState State to apply to address
   */
  public updateAddressValueAndState(
    address: Address,
    currentValue: string,
    currentState: string,
  ): Address {
    address.lastValueTimestamp = moment().format(
      MomentFormats.MOMENT_DATETIME_FORMAT_SHORT,
    );

    var precedentLastValue: string = address.lastValue;
    var precedentLastState: string = address.lastState;

    address.lastValue = currentValue;
    address.lastTechValue = currentValue;
    // GF: if the state is empty (write message has state empty) we need to recalculate it?
    address.lastState = currentState || currentValue;

    this._loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      "_mqttService.subscribeTopic",
      "handleMQTTSubscriptioByAddress",
      "UPDATE TO ADDRESS:" +
        address.address +
        " | NAME: " +
        address.name +
        " | PRECEDENT LASTVALUE: " +
        precedentLastValue +
        " | NEW LASTVALUE: " +
        address.lastValue +
        " | PRECEDENT LASTSTATE: " +
        precedentLastState +
        " | NEW LASTSTATE: " +
        address.lastState,
    );
    this.addressUpdate.next(address);

    return address;
  }

  //#region Mqtt addresses handling

  /**
   *  This method ask the "mqtt-client.service" to subscribe to peculiar MQTT address; this MQTT address is identified by info carried in the
   * given Address object. This method also handles the creation of a "local" subscription to the Observable generated as result to a succesffull
   * MQTT subscription.
   *
   * @param address An "Address" object which contains the infos (instanceGUID and address) identifying a peculiar MQTT address
   */
  private async handleMQTTSubscriptioByAddress(
    address: Address,
  ): Promise<void> {
    const keyAddress: string = address.instanceGUID + "_" + address.address;

    const observableTopicAddress =
      await this._mqttService.subscribeTopic(keyAddress);

    this._mqttAddressSubscriptions.push(
      observableTopicAddress
        .pipe(
          map((message: IMqttMessage) => {
            return this.parseMqttMessage(message);
          }),
          filter((message: any) => {
            return this.filterCurrentUserLanguge(message);
          }),
          map((message: any) => message.decodedPayload),
        )
        .subscribe((mqttMessage: any) => {
          // GF: I catch only the messages arriving from the server as value update, not the one used to READ
          if (
            mqttMessage.Message.Action === undefined ||
            mqttMessage.Message.Action === AddressDefaultMethods.WRITE
          ) {
            address = this.updateAddressValueAndState(
              address,
              mqttMessage.Message.Value,
              mqttMessage.Message.State,
            );
          }
        }),
    );
  }

  subscribeMQTTAddresses(): void {}

  /**
   * This method handles the subscription to mqtt topics which have been requested by other COMPONENTS/PAGES, not by SERVICES.
   * It does so by creating a local "safety" subscription to the associated Observables, then returning these Observables to
   * the requesting component. In this way, while the component shall unsubscribe from the Obsdervable normally at its destruction, the
   * mqtt native client shall mantain the subscription to the topic, until the subscription in this service will be unsubscribed too.
   * This shall happen in two situation:
   *  + At destruction of service/closure of app.
   *  + At logout.
   * @param addressString
   * @returns
   */
  public async registerExternalMQTTRequest(
    addressString: string,
  ): Promise<Observable<IMqttMessage>> {
    const addressObservable: Observable<IMqttMessage> =
      await this._mqttService.subscribeTopic(addressString);

    const localSub = addressObservable.subscribe();

    await this.checkLocalAddressSubscriptionMap(addressString, localSub);

    return addressObservable;
  }

  public async checkLocalAddressSubscriptionMap(
    addressToCheck: string,
    addressSubscription: Subscription,
  ): Promise<boolean> {
    let result: boolean = false;
    if (this._mqttExternalAddressSubscriptionsMap.has(addressToCheck)) {
      addressSubscription.unsubscribe();

      this._loggerService.log(
        LogLevel.LOG_INFO,
        this._logContext,
        this._logContext,
        "checkLocalAddressSubscriptionMap()",
        "Address ALREADY present " + addressToCheck,
      );
    } else {
      this._mqttExternalAddressSubscriptionsMap.set(
        addressToCheck,
        addressSubscription,
      );

      this._loggerService.log(
        LogLevel.LOG_INFO,
        this._logContext,
        this._logContext,
        "checkLocalAddressSubscriptionMap()",
        "NEW address: " + addressToCheck,
      );
      result = true;
    }
    return result;
  }

  /**
   * This method delete the "safety" subscriptions to the Observables (returned by mqtt-client.service) related to
   * the mqtt topics subription to whom has been requested by componet/pages.
   */
  private async deleteExternalMQTTRequest(): Promise<void> {
    for (const entry of Array.from(
      this._mqttExternalAddressSubscriptionsMap.entries(),
    )) {
      try {
        entry[1].unsubscribe();

        this._mqttExternalAddressSubscriptionsMap.delete(entry[0]);
        // }
      } catch (exception) {
        this._loggerService.log(
          LogLevel.LOG_ERROR,
          this._logContext,
          this._logContext,
          "unsubscribeToMQTTAddresses()",
          "ERROR:  " + exception.message,
        );
      }
    }
  }

  /* NOTE: Dummy implementation */
  /*
  async checkLocalAddressSubscriptionMap(
    addressToCheck: string,
    addressSubscription: Subscription
  ): Promise<boolean> {
    return true;
  }
  */

  async unsubscribeToMQTTAddresses(): Promise<void> {
    await this.deleteExternalMQTTRequest();

    await this.clearLocalSubscriptionstoAddresses();

    for (const keyAddressPair of Array.from(this._addressStore.entries())) {
      this._addressStore.delete(keyAddressPair[0]);
    }

    //this._addressStore = new Map<string, Address>(null);

    /* CURRENTLY THIS FEATURE IS NOT USED */
    //this._addressSubject = new BehaviorSubject<Map<string, Address>>(null);
    //this._addressSubject.next(this._addressStore);
  }

  async clearLocalSubscriptionstoAddresses() {
    for (const subToAddress of this._mqttAddressSubscriptions) {
      subToAddress.unsubscribe();
    }

    this._mqttAddressSubscriptions = new Array<Subscription>();
  }
  //#endregion

  /**
   * Parse generig IMQTT message object into specific object
   *
   * @param message Message to parse
   * @returns
   */
  private parseMqttMessage(message: any): object {
    (message as any).decodedPayload = JSON.parse(message.payload.toString());

    return message;
  }

  /**
   * Check if current message topic is current user/language address:
   * - IF not, forward in any case mqtt message
   * - If Yes, check if current clientID message address. If equal to client,
   * it forward message, otherwiser block message
   *
   * @param message Message to check
   * @returns
   */
  private filterCurrentUserLanguge(message: any) {
    let forwardMessage: boolean = true;
    const currentUserLanguageAddress: string =
      AppSettings.INSTANCE_GUID_VIRTUAL_VARIABLE +
      "_" +
      AppReserved.ADDRESS_LANGUAGE;

    if (
      message.decodedPayload.Topic === currentUserLanguageAddress &&
      message.decodedPayload.Message.ClientID !== this._mqttService.clientID
    ) {
      forwardMessage = false;
    }

    return forwardMessage;
  }
}
